import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GenerationCard } from '@gilding/shared';
import { STYLE_PROFILES } from '@gilding/shared';
import { api } from '../api/client.js';
import { AddressBadge } from '../components/Identicon.js';
import { Reveal } from '../components/Reveal.js';
import { GildedImage } from '../components/GildedImage.js';
import { ShareButton } from '../components/ShareButton.js';
import { cn } from '../lib/classnames.js';

// -----------------------------------------------------------------------------
// Inline SVG glyphs — bespoke, unicode-free. Chevron for upvote, tight spiral
// for remix count, an open book for the empty state. Sized 1em so they scale
// with the surrounding type.
// -----------------------------------------------------------------------------

/** Solid upward triangle — fills with the surrounding text colour. One stable
 *  node (no keyed remount), so the pop animation can't double-paint. */
function Caret({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" className={className}>
      <path d="M8 4 L13.2 11 L2.8 11 Z" fill="currentColor" />
    </svg>
  );
}

/** Remix count mark — a new gilded "impression" lifting off the source piece:
 *  two offset plates, the source in ink and the remix edged in gold, struck
 *  with a small gilded sparkle. The gold plate lifts on hover (the parent span
 *  is a `group`), so making a remix reads as pressing a fresh leaf of gold. */
function RemixMark() {
  return (
    <svg viewBox="0 0 18 18" width="15" height="15" aria-hidden="true" className="overflow-visible">
      {/* The new gilded impression — gold-edged plate that lifts on hover. */}
      <rect
        x="6.6"
        y="2"
        width="9"
        height="9"
        rx="2"
        fill="rgb(var(--color-surface))"
        stroke="rgb(var(--color-accent))"
        strokeWidth="1.3"
        className="origin-center transition-transform duration-300 ease-out motion-safe:group-hover:translate-x-[1.2px] motion-safe:group-hover:-translate-y-[1.2px]"
      />
      {/* The source plate — ink, filled so the overlap reads cleanly. */}
      <rect
        x="2.5"
        y="6.6"
        width="9"
        height="9"
        rx="2"
        fill="rgb(var(--color-surface))"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      {/* A struck-gold sparkle on the source — the gilding being carried over. */}
      <path
        d="M7 9.7 l0.55 1.35 1.35 0.55 -1.35 0.55 -0.55 1.35 -0.55 -1.35 -1.35 -0.55 1.35 -0.55 Z"
        fill="rgb(var(--color-accent))"
      />
    </svg>
  );
}

function EmptyFolio() {
  // A closed manuscript with a gilded clasp — the page's signature emptiness.
  return (
    <svg
      viewBox="0 0 120 120"
      width="120"
      height="120"
      aria-hidden="true"
      className="text-ink-soft"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
        <rect x="22" y="20" width="76" height="86" rx="2" />
        <path d="M30 28 L30 98" opacity="0.55" />
        <path d="M34 36 L88 36" opacity="0.35" />
        <path d="M34 46 L78 46" opacity="0.35" />
        <path d="M34 56 L82 56" opacity="0.35" />
        <path d="M34 66 L70 66" opacity="0.35" />
      </g>
      {/* Gilded clasp on the right edge */}
      <g>
        <rect
          x="92"
          y="58"
          width="12"
          height="10"
          rx="1.5"
          fill="rgb(var(--color-accent) / 0.9)"
          stroke="rgb(var(--color-accent-deep))"
          strokeWidth="0.6"
        />
        <circle cx="98" cy="63" r="1.4" fill="rgb(var(--color-accent-deep))" />
      </g>
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Attribution moved to components/Identicon.tsx (AddressBadge). One canonical
// identicon language across Feed, Remix, Leaderboard — gold-on-parchment 5x5
// grid, never random HSL that could vanish against a surface.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Upvote — pill-free, icon + count. Chevron fills on hover, count animates
// up on success via a key bump.
// -----------------------------------------------------------------------------

function UpvoteButton({
  count,
  voted,
  pending,
  onClick,
}: {
  count: number;
  voted: boolean;
  pending: boolean;
  onClick: () => void;
}) {
  // A single stable caret node toggled with a class — no keyed remount, so the
  // pop can't double-paint. `pop` clears itself on animationend so the same
  // node re-animates on every tap.
  const [pop, setPop] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        if (!voted) setPop(true);
        onClick();
      }}
      onAnimationEnd={() => setPop(false)}
      disabled={pending}
      aria-pressed={voted}
      aria-label={voted ? `Upvoted, ${count}` : `Upvote, ${count}`}
      className={cn(
        'inline-flex select-none items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors duration-200 disabled:opacity-50',
        voted
          ? 'border-accent/45 bg-accent/10 text-accent'
          : 'border-hairline/70 text-ink-soft hover:border-accent/40 hover:text-ink',
      )}
    >
      <Caret className={cn('shrink-0', pop && 'vote-pop')} />
      <span className="font-display text-micro tabular-nums leading-none">{count}</span>
    </button>
  );
}

// -----------------------------------------------------------------------------

export function Feed() {
  const queryClient = useQueryClient();
  const feedQuery = useQuery({
    queryKey: ['feed'],
    queryFn: () => api.feed(),
  });

  const [voteNote, setVoteNote] = useState<string | null>(null);
  type FeedData = { items: GenerationCard[]; total: number };
  const upvoteMut = useMutation({
    mutationFn: (id: string) => api.upvote(id),
    // Optimistically toggle the vote in the cache so the pill responds
    // instantly; reconcile with the server on settle.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['feed'] });
      const prev = queryClient.getQueryData<FeedData>(['feed']);
      queryClient.setQueryData<FeedData>(['feed'], (old) =>
        old
          ? {
              ...old,
              items: old.items.map((it) =>
                it.id === id
                  ? {
                      ...it,
                      viewerVoted: !it.viewerVoted,
                      upvotes: Math.max(0, it.upvotes + (it.viewerVoted ? -1 : 1)),
                    }
                  : it,
              ),
            }
          : old,
      );
      return { prev };
    },
    onSuccess: () => setVoteNote(null),
    onError: (e: unknown, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['feed'], ctx.prev);
      const code = (e as { code?: string })?.code;
      setVoteNote(
        code === 'self_vote'
          ? 'You can’t upvote your own gilding. The ranks only count votes from others.'
          : code === 'not_holder'
            ? 'Voting is for collectors. Hold at least one NFT in any collection for your vote to count.'
            : code === 'unauthenticated'
              ? 'Connect your wallet to vote.'
              : 'Could not register your vote. Try again.',
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      void queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });

  const items = (feedQuery.data?.items ?? []) as GenerationCard[];

  return (
    <div className="flex flex-col gap-10">
      {/* Page signature — h1 alone. The eyebrow is reserved for Landing + Leaderboard. */}
      <Reveal as="header" className="flex flex-col gap-3">
        <h1 className="heading-display">The feed</h1>
        <p className="max-w-prose font-display italic text-caption text-ink-subtle">
          Published gildings from across every collection. Remix one with your own
          character to read the prompt that did the work.
        </p>
      </Reveal>

      {voteNote && (
        <div
          role="status"
          className="flex items-start justify-between gap-4 rounded-sm border border-hairline-strong/60 bg-surface-elevated px-4 py-3"
        >
          <p className="font-display italic text-caption text-ink-soft">{voteNote}</p>
          <button
            type="button"
            className="font-display text-caption text-ink-subtle transition-colors hover:text-ink"
            onClick={() => setVoteNote(null)}
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {feedQuery.isLoading && (
        <p className="font-display italic text-ink-subtle">Gathering the latest pages&hellip;</p>
      )}

      {!feedQuery.isLoading && items.length === 0 && (
        <section className="flex flex-col items-center gap-5 py-12 text-center">
          <EmptyFolio />
          <div className="flex flex-col gap-2">
            <h2 className="heading-section">No pages bound yet</h2>
            <p className="max-w-sm text-ink-soft">
              When someone publishes their first gilding to the feed, it lands here.
              That someone could be you.
            </p>
          </div>
          <Link to="/new" className="btn-primary">
            Begin a gilding
          </Link>
        </section>
      )}

      {items.length > 0 && (
        /* Masonry columns: every piece keeps its own aspect ratio, so the
           feed reads like a wall of prints rather than a grid of crops. */
        <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">
          {items.map((it, i) => (
            <Reveal
              key={it.id}
              delay={Math.min(i, 5) * 70}
              className="mb-8 break-inside-avoid"
            >
              <FeedEntry
                item={it}
                voted={it.viewerVoted ?? false}
                onUpvote={() => upvoteMut.mutate(it.id)}
                pending={upvoteMut.isPending && upvoteMut.variables === it.id}
              />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// FeedEntry — image is the hero. Meta lives below as one .tag line plus an
// attribution line. Actions sit on a hairline-divided footer. No card chrome,
// no enum pills, no left rail.
// -----------------------------------------------------------------------------

function FeedEntry({
  item,
  voted,
  onUpvote,
  pending,
}: {
  item: GenerationCard;
  voted: boolean;
  onUpvote: () => void;
  pending: boolean;
}) {
  const styleLabel = STYLE_PROFILES[item.stylePreset]?.label ?? 'Untitled style';
  return (
    <article className="group flex flex-col gap-3">
      <Link
        to={`/remix/${item.id}`}
        className="card-plate plate-lift block overflow-hidden"
      >
        {/* The image defines its own height. The declared ratio (with the
            `auto` keyword: natural ratio wins once loaded) only reserves
            space while loading — a hard reserved box + object-cover leaves
            a sunken band inside the plate when the render disagrees. */}
        <GildedImage
          src={item.thumbnail}
          alt="Published gilding"
          className="block h-auto w-full group-hover:scale-[1.02]"
          style={{ aspectRatio: `auto ${item.aspectRatio.replace(':', ' / ')}` }}
          loading="lazy"
        />
      </Link>

      {/* Tight meta block — italic display serif tag, then attribution on its
          own line so the eye reads style first, maker second. */}
      <div className="flex flex-col gap-1 px-1">
        <span className="tag">
          <span className="tag-dot" />
          {styleLabel}
        </span>
        <AddressBadge address={item.ownerAddress} displayName={item.ownerDisplayName} />
      </div>

      {/* Hairline-divided action row. Upvote on the left, remix CTA + remix
          count on the right. No full-width destructive button, no underlines. */}
      <div className="flex items-center justify-between gap-3 border-t border-hairline/60 pt-3">
        <div className="flex items-center gap-2">
          <UpvoteButton count={item.upvotes} voted={voted} pending={pending} onClick={onUpvote} />
          <ShareButton id={item.id} variant="pill" />
        </div>
        <div className="flex items-center gap-3">
          <span
            className="group inline-flex items-center gap-1.5 text-caption text-ink-subtle"
            title={`${item.remixes} remix${item.remixes === 1 ? '' : 'es'}`}
          >
            <RemixMark />
            <span className="tabular-nums">{item.remixes}</span>
          </span>
          <span className="hairline-v h-4" />
          <Link to={`/remix/${item.id}`} className="btn-ghost">
            Remix this
          </Link>
        </div>
      </div>
    </article>
  );
}
